/**
 * TimelineMarkerChip
 *
 * Small inline visual representations of timeline elements (intro card, end card,
 * nametag) that surface in the clip transcript view at the moment they appear
 * in the video. Hover renders a rich tooltip; when an `onClick` handler is
 * provided (PLAT-9968 — clip-preview surface), the chip becomes clickable and
 * forwards the marker + its DOM rect so the parent can anchor a popover.
 * `user-select: none` keeps their text out of selection ranges that pass
 * through them, but the pointer events still fire on click.
 */

import { TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { useSpeakerLabels } from '@src/hooks/useSpeakerLabels';
import React, { useCallback, useId, useRef } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import type { TimelineMarker } from './hooks/useTimelineMarkers';

export type MarkerClickHandler = (
  marker: TimelineMarker,
  anchorElement: HTMLElement,
) => void;

type CardChipProps = {
  marker: TimelineMarker;
  onClick?: MarkerClickHandler;
};

export const CardChip: React.FC<CardChipProps> = ({ marker, onClick }) => {
  const isIntro = marker.type === 'introCard';
  const typeLabel = isIntro ? 'Intro card' : 'End card';
  const tooltip = marker.label ? `${typeLabel}: ${marker.label}` : typeLabel;
  const tooltipId = useId();
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      if (!onClick) return;
      e.stopPropagation();
      const el = wrapperRef.current;
      if (!el) return;
      onClick(marker, el);
    },
    [marker, onClick],
  );
  return (
    <>
      <CardChipWrapper
        ref={wrapperRef}
        data-marker-type={marker.type}
        data-tooltip-id={tooltipId}
        data-tooltip-content={tooltip}
        $clickable={!!onClick}
        onClick={onClick ? handleClick : undefined}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  const el = wrapperRef.current;
                  if (el) onClick(marker, el);
                }
              }
            : undefined
        }
      >
        <CardGlyph>{isIntro ? '▶' : '■'}</CardGlyph>
        <CardLabel>{isIntro ? 'Intro' : 'End'}</CardLabel>
      </CardChipWrapper>
      <Tooltip
        id={tooltipId}
        style={{
          ...TOOLTIP_STYLE,
          maxWidth: '280px',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          userSelect: 'none',
        }}
      />
    </>
  );
};

type NametagChipProps = {
  marker: TimelineMarker;
  onClick?: MarkerClickHandler;
};

export const NametagChip: React.FC<NametagChipProps> = ({
  marker,
  onClick,
}) => {
  const { getSpeakerName, getSpeakerColor, getSpeakerTitle } =
    useSpeakerLabels();
  const speakerLabel = marker.speakerLabel;
  const name = speakerLabel ? getSpeakerName(speakerLabel) : 'Speaker';
  const color = speakerLabel ? getSpeakerColor(speakerLabel) : undefined;
  const title = speakerLabel ? getSpeakerTitle(speakerLabel) : undefined;
  const tooltip = title ? `${name}\n${title}` : name;
  const tooltipId = useId();
  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      if (!onClick) return;
      e.stopPropagation();
      const el = wrapperRef.current;
      if (!el) return;
      onClick(marker, el);
    },
    [marker, onClick],
  );
  return (
    <>
      <NametagWrapper
        ref={wrapperRef}
        data-marker-type={marker.type}
        data-tooltip-id={tooltipId}
        data-tooltip-content={tooltip}
        $clickable={!!onClick}
        onClick={onClick ? handleClick : undefined}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  const el = wrapperRef.current;
                  if (el) onClick(marker, el);
                }
              }
            : undefined
        }
      >
        <NametagDot $accent={color} />
        <NametagText>Nametag</NametagText>
      </NametagWrapper>
      <Tooltip
        id={tooltipId}
        style={{
          ...TOOLTIP_STYLE,
          maxWidth: '280px',
          whiteSpace: 'pre-line',
          wordBreak: 'break-word',
          userSelect: 'none',
        }}
      />
    </>
  );
};

const ChipBase = styled.span<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  /* Centred on the line, not baseline-aligned. An inline-flex box takes its
     baseline from its FIRST flex item, which here is the dot — a span with no
     text and therefore no baseline of its own, so the browser synthesises one
     from the box's bottom edge and the whole chip hangs below the words it sits
     among. Centring sidesteps the synthesis entirely. */
  vertical-align: middle;
  margin: 0 4px;
  font-size: 11px;
  line-height: 1;
  user-select: none;
  white-space: nowrap;
  cursor: ${p => (p.$clickable ? 'pointer' : 'default')};

  ${p =>
    p.$clickable
      ? `
        &:hover {
          background: rgba(255, 255, 255, 0.14);
          border-color: rgba(255, 255, 255, 0.22);
        }
        &:focus-visible {
          outline: 2px solid var(--primary);
          outline-offset: 1px;
        }
      `
      : ''}
`;

const CardChipWrapper = styled(ChipBase)`
  gap: 3px;
  padding: 3px 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
`;

const CardGlyph = styled.span`
  font-size: 8px;
  line-height: 1;
  color: var(--text-muted);
`;

const CardLabel = styled.span``;

const NametagWrapper = styled(ChipBase)`
  gap: 4px;
  padding: 3px 7px 3px 5px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
`;

const NametagDot = styled.span<{ $accent?: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.$accent || 'var(--text-muted)'};
  display: inline-block;
`;

const NametagText = styled.span``;
